import { StageData, Side, TimeMeta, Segment } from '../types';
import ButterflyBar from './ButterflyBar';

interface Props {
  stage: StageData;
  // Shared denominator for bar widths — passed down from PatientStatus so
  // that every bar across every stage is normalized to the same reference
  // (the largest side count across all stages). Ensures Stage 3 left looks
  // ~14× bigger than Stage 1 left, reflecting real population sizes.
  referenceTotal: number;
  selectedSides: { right: boolean; left: boolean };
  onSideClick?: (side: Side) => void;
  onSideToggle?: (side: Side) => void;
}

const fmt = (n: number) => n.toLocaleString('en-US');

function timeNode(time: TimeMeta | null) {
  if (!time) return null;
  return (
    <span className="bf-meta-time">
      {time.text}
      {time.tooltip && (
        <span className="info-icon" title={time.tooltip}>{' '}ⓘ</span>
      )}
    </span>
  );
}

interface SideBlockProps {
  side: Side;
  segments: Segment[];
  referenceTotal: number;
  count: number;
  label: string;
  time: TimeMeta | null;
  selected: boolean;
  onMetaClick?: () => void;
  onToggle?: () => void;
}

function SideBlock({ side, segments, referenceTotal, count, label, time, selected, onMetaClick, onToggle }: SideBlockProps) {
  return (
    <div className={`bf-side bf-${side}${selected ? ' selected' : ''}`}>
      <ButterflyBar segments={segments} referenceTotal={referenceTotal} />
      <div className="bf-side-meta">
        <span className="bf-meta-row">
          <button
            type="button"
            className={`bf-segment-toggle${selected ? ' checked' : ''}`}
            aria-pressed={selected}
            title={selected ? 'הסר מהבחירה' : 'הוסף לבחירה'}
            onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
          >
            {selected ? '✓' : ''}
          </button>
          <span
            className="bf-meta-clickable drillable"
            onClick={onMetaClick}
            title="לחץ לפתיחת רשימת מטופלים"
          >
            <span className="bf-side-count num-eng">{fmt(count)}</span>
            <span className="bf-side-label">{label}</span>
            <span className="drill-arrow" aria-hidden="true">↗</span>
          </span>
        </span>
        {timeNode(time)}
      </div>
    </div>
  );
}

export default function StageRow({ stage, referenceTotal, selectedSides, onSideClick, onSideToggle }: Props) {
  return (
    <div className="stage-row">
      <div className="stage-row-head">
        <span className="stage-name">
          {stage.name}
          {stage.tooltip && (
            <span className="info-icon" title={stage.tooltip}>{' '}ⓘ</span>
          )}
        </span>
        <span className="stage-stats">
          · <span className="num-eng">{fmt(stage.total)}</span> ·{' '}
          <span className="num-eng">{stage.pct_of_active.toFixed(2)}%</span>
        </span>
      </div>
      <div className="butterfly">
        <SideBlock
          side="right"
          segments={stage.right.segments}
          referenceTotal={referenceTotal}
          count={stage.right.count}
          label={stage.right.label}
          time={stage.right.time}
          selected={selectedSides.right}
          onMetaClick={() => onSideClick?.('right')}
          onToggle={() => onSideToggle?.('right')}
        />
        <div className="bf-center" />
        <SideBlock
          side="left"
          segments={stage.left.segments}
          referenceTotal={referenceTotal}
          count={stage.left.count}
          label={stage.left.label}
          time={stage.left.time}
          selected={selectedSides.left}
          onMetaClick={() => onSideClick?.('left')}
          onToggle={() => onSideToggle?.('left')}
        />
      </div>
    </div>
  );
}
